import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Users, Send, TrendingUp, CheckCircle, ArrowUpRight,
  ChevronRight, Info, Building2, Loader2, CreditCard, Phone, RefreshCw,
  Megaphone, ShoppingCart, Zap, Globe, CalendarCheck, MessageCircle,
  Bot, LayoutTemplate, Workflow, UserCog, Package, AlertTriangle,
  BarChart3, Activity, Shield, Bell, Plus, X, Sparkles,
  ArrowUp, ArrowDown, Calendar, Eye, Clock, Target,
  Rocket, Brain, TrendingDown, Minus, ChevronDown,
} from 'lucide-react';
// recharts sparkline removed — was rendering Math.sin-derived synthetic data.
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ConnectMetaModal } from '@/components/business-account/ConnectMetaModal';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/components/AuthProvider';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '@/lib/api';
import { useMetaTemplates } from '@/hooks/useMetaTemplates';
import { useWhatsAppFlows } from '@/hooks/useWhatsAppFlows';
import { TeamService } from '@/lib/api/services/teamService';
import { useAiAgents } from '@/hooks/useAiAgents';
import { useShopifyOrders } from '@/hooks/useShopifyOrders';
import { useWooCommerceOrders } from '@/hooks/useWooCommerceOrders';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { useBusinessAccounts } from '@/hooks/useBusinessAccounts';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
// useCampaigns removed — replaced by server-aggregated useCampaignAnalytics.
import { useMessagingUsage } from '@/hooks/useMessagingUsage';
import type { MessagingLimitInfo } from '@/lib/api/services/meta';
import { useAdsSetupStatus, useAdsCampaigns, useAdsBalance } from '@/hooks/useAds';
import { useCommerceStats } from '@/hooks/useCommerce';
import { useAutomationFlows } from '@/hooks/useAutomation';
import { useLiveChatChannels } from '@/hooks/useLiveChat';
import { useEventTypes } from '@/hooks/useScheduling';
import { useRazorpayConnection } from '@/hooks/useRazorpayConnection';
import { useOverviewAnalytics, useCampaignAnalytics } from '@/hooks/useAnalytics';
import { useContactStats } from '@/hooks/useContacts';
import { usePendingOnboardingAssets } from '@/hooks/usePendingOnboardingAssets';
import { SetupProgress, type SetupStep } from '@/components/dashboard/SetupProgress';
import { MobileAppCard } from '@/components/dashboard/MobileAppCard';
import { WorkspaceFeatures } from '@/components/dashboard/WorkspaceFeatures';
import { DemoBookingEmbed } from '@/components/scheduling/DemoBookingEmbed';
import whatsapp_icon from '/assets/social-media/whatsapp-icon.svg';
import rabbit_icon from '/assets/home/skating-rabbit.svg';

function useDashboardStats(businessAccountId: string | null) {
  return useQuery({
    queryKey: ['dashboard-stats', businessAccountId],
    queryFn: () => dashboardApi.getStats(businessAccountId),
    // Always re-fetch on mount + window-focus so the user never sees a value
    // that's older than the current page view. gcTime kept high so back/forward
    // navigation still feels instant while the refetch is in flight.
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    enabled: !!businessAccountId,
  });
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function getTimestamp(): string {
  return new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatRelativeTimestamp(value?: string | null): string {
  if (!value) return 'Not updated yet';

  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return 'Not updated yet';

  const diffMs = Date.now() - timestamp;
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'Updated just now';
  if (diffMinutes < 60) return `Updated ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `Updated ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `Updated ${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
}

// Date range presets for the dashboard analytics window.
// All "trend" deltas + KPI values are scoped to the selected range and a real
// previous-equal-length period — no synthesized data anywhere on this page.
const DATE_RANGE_PRESETS: { key: '7d' | '30d' | '90d'; label: string; days: number }[] = [
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
];

function buildDateRange(days: number): { current: { start: string; end: string }; previous: { start: string; end: string } } {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);

  const prevEnd = new Date(start);
  prevEnd.setDate(prevEnd.getDate() - 1);
  prevEnd.setHours(23, 59, 59, 999);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - (days - 1));
  prevStart.setHours(0, 0, 0, 0);

  return {
    current: { start: start.toISOString(), end: end.toISOString() },
    previous: { start: prevStart.toISOString(), end: prevEnd.toISOString() },
  };
}

// Real period-over-period delta. Returns null when previous period is 0 or
// missing — the UI hides the trend badge in that case rather than showing a
// fabricated +100% / +∞ figure.
function computeDelta(current: number | null | undefined, previous: number | null | undefined): number | null {
  if (current == null || previous == null) return null;
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

// ── AI Insight Card ───────────────────────────────────────────────────────────

interface AiInsightTokenUsage {
  total_tokens?: number | null;
  total_cost_inr?: number | null;
  charged_cost_inr?: number | null;
  credits_added?: number | null;
}

interface AiInsight {
  analysisDate: string | null;
  status: string | null;
  summary: string | null;
  aiInsights: string[];
  metrics: {
    total_conversations?: number | null;
    analyzed_conversations?: number | null;
    success_conversations?: number | null;
    failure_conversations?: number | null;
    neutral_conversations?: number | null;
    success_rate_percent?: number | null;
    failure_rate_percent?: number | null;
    neutral_rate_percent?: number | null;
    token_usage?: AiInsightTokenUsage | null;
  } | null;
  error: string | null;
  triggeredAt: string | null;
  updatedAt: string | null;
}

function formatInsightDate(dateStr: string | null): string {
  if (!dateStr) return '';

  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const d = isDateOnly ? new Date(`${dateStr}T00:00:00`) : new Date(dateStr);

  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

function AiInsightCard({ insight }: { insight: AiInsight }) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [minimized, setMinimized] = useState(true);
  const isPending = insight.status === 'PENDING';
  const isFailed = insight.status === 'FAILED';
  const isSuccess = insight.status === 'SUCCESS';

  const m = insight.metrics;
  const totalConvs   = m?.total_conversations   ?? 0;
  const analyzedConvs = m?.analyzed_conversations ?? null;
  const successCount = m?.success_conversations  ?? 0;
  const failureCount = m?.failure_conversations  ?? 0;
  const neutralCount = m?.neutral_conversations  ?? 0;
  const successRate  = m?.success_rate_percent   ?? 0;
  const failureRate  = m?.failure_rate_percent   ?? 0;
  const neutralRate  = m?.neutral_rate_percent   ?? 0;

  const bullets = insight.aiInsights?.length
    ? insight.aiInsights
    : insight.summary ? [insight.summary] : [];

  const statCols = [
    { label: 'Total',   count: totalConvs,   rate: 100,         accent: '#64748B', text: 'text-slate-700',   icon: MessageSquare },
    { label: 'Success', count: successCount, rate: successRate, accent: '#10B981', text: 'text-emerald-700', icon: TrendingUp },
    { label: 'Failed',  count: failureCount, rate: failureRate, accent: '#EF4444', text: 'text-red-600',     icon: TrendingDown },
    { label: 'Neutral', count: neutralCount, rate: neutralRate, accent: '#94A3B8', text: 'text-slate-500',   icon: Minus },
  ];

  const splitSegments = totalConvs > 0
    ? [
        { color: '#10B981', value: successCount },
        { color: '#94A3B8', value: neutralCount },
        { color: '#EF4444', value: failureCount },
      ]
    : [];

  const hasNoData = !isPending && !isFailed && !isSuccess;

  return (
    <Card className="border border-slate-200 shadow-md bg-white rounded-2xl overflow-hidden">
      <CardContent className="p-0">
        {/* Header — dark gradient band */}
        <div
          className="relative px-5 py-4 flex items-center justify-between cursor-pointer select-none overflow-hidden"
          onClick={() => setMinimized(m => !m)}
          style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 50%, #4338CA 100%)' }}
        >
          {/* Decorative glow */}
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-violet-500/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 left-1/3 w-40 h-40 rounded-full bg-indigo-400/10 blur-3xl pointer-events-none" />

          <div className="relative flex items-center gap-3.5">
            <div className="relative h-11 w-11 rounded-xl bg-gradient-to-br from-violet-400 to-indigo-500 flex items-center justify-center shrink-0 shadow-lg shadow-indigo-900/40 ring-1 ring-white/20">
              <Brain className="h-5.5 w-5.5 text-white" />
              <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-indigo-900 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-[18px] font-bold text-white tracking-tight">AI Lead Insights</h3>
                <span className="text-[10px] font-bold text-violet-200 bg-white/10 border border-white/20 rounded-md px-1.5 py-0.5 tracking-wider uppercase">Beta</span>
              </div>
              <p className="text-[12px] text-violet-200/90 font-medium mt-0.5 flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 text-violet-300" />
                {insight.analysisDate
                  ? `${formatInsightDate(insight.analysisDate)}${analyzedConvs != null ? ` · ${analyzedConvs} conversations analysed` : ''}`
                  : formatRelativeTimestamp(insight.updatedAt)}
              </p>
            </div>
          </div>

          <div className="relative flex items-center gap-2">
            {isPending && (
              <span className="flex items-center gap-1.5 text-[11px] font-bold text-amber-100 bg-amber-500/20 border border-amber-300/40 rounded-full px-3 py-1 backdrop-blur-sm">
                <Loader2 className="h-3 w-3 animate-spin" /> Analysing
              </span>
            )}
            {isFailed && (
              <span className="text-[11px] font-bold text-red-100 bg-red-500/25 border border-red-300/40 rounded-full px-3 py-1 backdrop-blur-sm">
                Failed
              </span>
            )}
            {isSuccess && (
              <span className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-100 bg-emerald-500/25 border border-emerald-300/40 rounded-full px-3 py-1 backdrop-blur-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                Ready
              </span>
            )}
            {hasNoData && (
              <span className="text-[11px] font-bold text-slate-200 bg-white/10 border border-white/20 rounded-full px-3 py-1 backdrop-blur-sm">
                Awaiting first run
              </span>
            )}
            <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white/80 hover:bg-white/15 transition-colors">
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${minimized ? '-rotate-90' : ''}`} />
            </div>
          </div>
        </div>

        {!minimized && <>
        {/* Hero stat strip + segmented breakdown */}
        {isSuccess && totalConvs > 0 && (
          <div className="px-5 pt-5 pb-4 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center bg-gradient-to-b from-violet-50/40 to-transparent border-b border-slate-100">
            <div className="flex items-end gap-2">
              <p className="text-[56px] font-bold text-emerald-600 leading-none tracking-tighter">
                {successRate.toFixed(0)}
                <span className="text-[28px] text-emerald-500/80 align-top ml-0.5">%</span>
              </p>
              <div className="pb-1.5">
                <p className="text-[13px] font-bold text-slate-800 leading-tight">Success rate</p>
                <p className="text-[11px] text-slate-500 font-medium mt-0.5">{successCount} of {totalConvs} conversations</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Outcome split</span>
                <span className="text-[11px] font-semibold text-slate-500">{totalConvs} total</span>
              </div>
              <div className="flex h-3 w-full rounded-full overflow-hidden bg-slate-100 shadow-inner">
                {splitSegments.map((seg, i) => (
                  <div
                    key={i}
                    className="h-full transition-all duration-700"
                    style={{
                      width: `${(seg.value / totalConvs) * 100}%`,
                      backgroundColor: seg.color,
                    }}
                    title={`${seg.value}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-4 text-[11px] font-semibold">
                <span className="flex items-center gap-1.5 text-emerald-700"><span className="w-2 h-2 rounded-full bg-emerald-500" />Success {successRate.toFixed(0)}%</span>
                <span className="flex items-center gap-1.5 text-slate-500"><span className="w-2 h-2 rounded-full bg-slate-400" />Neutral {neutralRate.toFixed(0)}%</span>
                <span className="flex items-center gap-1.5 text-red-600"><span className="w-2 h-2 rounded-full bg-red-500" />Failed {failureRate.toFixed(0)}%</span>
              </div>
            </div>
          </div>
        )}

        {/* Conversation metric tiles — left accent bar style */}
        {(isSuccess || isPending) && (
          <div className="px-5 pt-4 pb-3 grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {statCols.map(({ label, count, rate, accent, text, icon: Icon }) => (
              <div
                key={label}
                className="relative rounded-lg bg-white border border-slate-200 px-3 py-2.5 flex flex-col gap-0.5 overflow-hidden hover:border-slate-300 hover:shadow-sm transition-all"
              >
                <span className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: accent }} />
                <div className="flex items-center justify-between mb-1 pl-1.5">
                  <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">{label}</span>
                  <Icon className={`h-3.5 w-3.5 ${text} opacity-70`} />
                </div>
                <p className={`text-[24px] font-bold leading-none tracking-tight pl-1.5 ${text}`}>{count}</p>
                <p className="text-[12px] font-bold mt-1 pl-1.5" style={{ color: accent }}>{(rate ?? 0).toFixed(1)}%</p>
              </div>
            ))}
          </div>
        )}

        {/* AI Insight accordion */}
        {bullets.length > 0 && (
          <div className="px-5 pt-2 pb-4">
            <div className="flex items-center gap-2 mb-2.5">
              <Sparkles className="h-3.5 w-3.5 text-violet-600" />
              <h4 className="text-[12px] font-bold text-slate-700 uppercase tracking-wider">AI Recommendations</h4>
              <span className="text-[11px] font-semibold text-slate-400">{bullets.length}</span>
            </div>
            <div className="space-y-2">
              {bullets.map((line, i) => {
                const isOpen = openIdx === i;
                const dotIdx = line.search(/[.!?]/);
                const title = dotIdx > 0 ? line.slice(0, dotIdx + 1) : line.slice(0, 80) + (line.length > 80 ? '…' : '');
                const hasMore = line.length > title.length;
                return (
                  <div key={i} className={`relative rounded-xl overflow-hidden transition-all ${isOpen ? 'border border-violet-300 shadow-sm shadow-violet-100' : 'border border-slate-200'}`}>
                    <span className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-violet-500 to-indigo-500" />
                    <button
                      onClick={() => setOpenIdx(isOpen ? null : i)}
                      className={`w-full flex items-start gap-3 pl-4 pr-3 py-3 text-left transition-colors ${isOpen ? 'bg-violet-50/60' : 'bg-white hover:bg-violet-50/40'}`}
                    >
                      <div className="h-6 w-6 rounded-md bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shrink-0 mt-0.5 shadow-sm shadow-violet-200">
                        <span className="text-[11px] font-bold text-white">{i + 1}</span>
                      </div>
                      <span className="flex-1 text-[14px] font-semibold text-slate-800 leading-snug">{title}</span>
                      {hasMore && (
                        <ChevronDown className={`h-4 w-4 text-violet-500 shrink-0 mt-1 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                      )}
                    </button>
                    {isOpen && hasMore && (
                      <div className="pl-4 pr-4 pb-3.5 pt-1 bg-violet-50/30 border-t border-violet-100">
                        <p className="text-[13px] text-slate-700 leading-relaxed pl-9">{line}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state — no analysis yet */}
        {hasNoData && bullets.length === 0 && (
          <div className="px-5 py-8 flex flex-col items-center text-center">
            <div className="h-14 w-14 rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 border border-violet-200/50 flex items-center justify-center mb-3">
              <Sparkles className="h-6 w-6 text-violet-500" />
            </div>
            <p className="text-[15px] font-bold text-slate-800 mb-1 tracking-tight">No insights yet</p>
            <p className="text-[13px] text-slate-500 max-w-sm leading-relaxed">
              Once you start having conversations, AI will analyse them daily and surface lead quality patterns here.
            </p>
          </div>
        )}

        {/* Pending — gentle wait state */}
        {isPending && bullets.length === 0 && totalConvs === 0 && (
          <div className="px-5 py-6 flex flex-col items-center text-center">
            <div className="h-12 w-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-3">
              <Loader2 className="h-5 w-5 text-amber-600 animate-spin" />
            </div>
            <p className="text-[14px] font-bold text-slate-800 mb-1 tracking-tight">Analysing conversations</p>
            <p className="text-[12px] text-slate-500 max-w-sm leading-relaxed">
              The AI is reviewing your recent threads. This usually takes a minute.
            </p>
          </div>
        )}

        {/* Failed state */}
        {isFailed && insight.error && (
          <div className="mx-5 my-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <div className="h-8 w-8 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <p className="text-[13px] font-bold text-red-900 mb-0.5">Analysis failed</p>
              <p className="text-[12px] font-medium text-red-700 leading-relaxed">{insight.error}</p>
            </div>
          </div>
        )}
        </>}
      </CardContent>
    </Card>
  );
}

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedAccountId, accounts, isLoading: accountsLoading } = useBusinessAccountContext();
  const { data: businessAccounts } = useBusinessAccounts();
  const { data: pendingOnboardingAssets, isLoading: pendingAssetsLoading } = usePendingOnboardingAssets();
  const { data: statsResponse } = useDashboardStats(selectedAccountId);
  // Campaign totals come from /analytics/campaigns (server-aggregated, date-filtered)
  // via useCampaignAnalytics below. The old useCampaigns(...).reduce(...) approach
  // silently undercounted orgs with > pageSize campaigns.
  const [quality, setQuality] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<string | null>(null);
  const [messagingUsageInfo, setMessagingUsageInfo] = useState<MessagingLimitInfo | null>(null);
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());
  const selectedAccount = businessAccounts?.find(account => account.id === selectedAccountId);

  const isAccountConnected = selectedAccount &&
    (selectedAccount.access_token_encrypted || selectedAccount.business_account_id);

  const {
    data: messagingUsageSummary,
    isLoading: isLoadingUsage,
    refetch: refetchUsage,
    isRefetching: isUsageRefetching
  } = useMessagingUsage(selectedAccount?.id);

  useEffect(() => {
    if (!isAccountConnected || !selectedAccount?.id) {
      setQuality(null);
      setPhoneNumber(null);
      setAccountStatus(null);
      setMessagingUsageInfo(null);
      return;
    }
    if (!messagingUsageSummary) return;
    const summaryData = messagingUsageSummary as Record<string, any>;
    const primaryNumber = summaryData?.phoneNumbers?.[0];
    const aggregateInfo = summaryData?.aggregate ?? null;
    setQuality(primaryNumber?.quality_rating || null);
    setPhoneNumber(primaryNumber?.display_phone_number || null);
    setAccountStatus(primaryNumber?.status || null);
    setMessagingUsageInfo(aggregateInfo);
  }, [isAccountConnected, messagingUsageSummary, selectedAccount?.id]);

  const formatNumber = useCallback((value: number | null | undefined) => {
    if (value === null || value === undefined) return '—';
    return new Intl.NumberFormat().format(value);
  }, []);

  const stats = statsResponse?.data;

  // ── Module Status Hooks ──
  const { data: adsSetupStatus, isLoading: adsStatusLoading } = useAdsSetupStatus();
  const adsConnected = !!(adsSetupStatus as any)?.connected || !!(adsSetupStatus as any)?.account?.ad_account_id;

  const { data: adsCampaignsData } = useAdsCampaigns({ page: 1, limit: 1 }, adsConnected);
  const adsCampaignCount = (adsCampaignsData as any)?.total || (adsCampaignsData as any)?.campaigns?.length || 0;

  const { data: commerceStats, isLoading: commerceLoading } = useCommerceStats();
  const commerceConnected = !!commerceStats && (commerceStats.total_orders > 0 || commerceStats.total_products > 0);

  const { data: automationData, isLoading: automationLoading } = useAutomationFlows({
    businessAccountId: selectedAccountId || undefined,
    limit: 1,
  });
  const automationTotal = (automationData as any)?.count || (automationData as any)?.total || ((automationData as any)?.data || []).length || 0;
  const automationConnected = automationTotal > 0;

  const { data: liveChatChannels, isLoading: liveChatLoading } = useLiveChatChannels(selectedAccountId || undefined);
  const liveChatConnected = Array.isArray(liveChatChannels) && liveChatChannels.length > 0;

  const { data: eventTypes, isLoading: schedulingLoading } = useEventTypes();
  const schedulingData = (eventTypes as any)?.data || eventTypes || [];
  const schedulingConnected = Array.isArray(schedulingData) && schedulingData.length > 0;

  const { data: razorpayConnected, isLoading: razorpayLoading } = useRazorpayConnection();

  const { data: templatesData, isLoading: templatesLoading } = useMetaTemplates(selectedAccountId || undefined);
  const templatesList = (templatesData as any)?.templates || [];
  const templatesCount = templatesList.length;
  const templatesApproved = templatesList.filter((t: any) => t.status?.toUpperCase() === 'APPROVED').length;
  const templatesConnected = templatesCount > 0;

  const { data: whatsappFlowsData, isLoading: whatsappFlowsLoading } = useWhatsAppFlows(selectedAccountId || undefined);
  const whatsappFlowsList = Array.isArray(whatsappFlowsData) ? whatsappFlowsData : [];
  const whatsappFlowsCount = whatsappFlowsList.length;
  const whatsappFlowsConnected = whatsappFlowsCount > 0;

  const { data: teamStatsData, isLoading: usersLoading } = useQuery({
    queryKey: ['team-stats'],
    queryFn: () => TeamService.getTeamStats(),
    staleTime: 5 * 60 * 1000,
  });
  const teamCount = (teamStatsData as any)?.data?.total_members ?? 0;

  const { data: aiAgentsData, isLoading: aiAgentsLoading } = useAiAgents(selectedAccountId || undefined);
  const aiAgentsList = Array.isArray(aiAgentsData) ? aiAgentsData : [];
  const aiAgentsCount = aiAgentsList.length;
  const aiAgentsActive = aiAgentsList.filter((a: any) => a.is_active || a.status === 'active').length;
  const aiAgentsConnected = aiAgentsCount > 0;

  const { data: shopifyData, isLoading: shopifyLoading } = useShopifyOrders({ filters: { page: 1, limit: 1 } });
  const shopifyTotal = (shopifyData as any)?.total || 0;
  const shopifyConnected = shopifyTotal > 0;

  const { data: wooData, isLoading: wooLoading } = useWooCommerceOrders({ filters: { page: 1, limit: 1 } });
  const wooTotal = (wooData as any)?.total || 0;
  const wooConnected = wooTotal > 0;

  const [rangeKey, setRangeKey] = useState<'7d' | '30d' | '90d'>('30d');
  const rangeDays = (DATE_RANGE_PRESETS.find(p => p.key === rangeKey)?.days ?? 30);
  const rangeLabel = DATE_RANGE_PRESETS.find(p => p.key === rangeKey)?.label ?? 'Last 30 days';
  const { current: currentRange, previous: previousRange } = useMemo(() => buildDateRange(rangeDays), [rangeDays]);

  const { data: overviewAnalytics, isLoading: analyticsLoading } = useOverviewAnalytics(
    selectedAccountId || undefined,
    currentRange
  );
  // Previous-period overview powers REAL trend deltas. Stays empty if backend errors —
  // the UI falls back to hiding the delta badge rather than fabricating one.
  const { data: previousOverview } = useOverviewAnalytics(
    selectedAccountId || undefined,
    previousRange
  );
  // Server-aggregated campaign analytics (NOT a reduce over a paginated list).
  const { data: campaignAnalytics, isLoading: campaignAnalyticsLoading } = useCampaignAnalytics(
    selectedAccountId || undefined,
    currentRange
  );

  // The range scopes newContactsInRange, which powers the range-aware "Total Contacts"
  // card + its trend delta. (The all-time total is still available as `totalContacts`
  // and used by the setup/health section lower on the page.)
  const { data: contactStatsData, isLoading: contactStatsLoading } = useContactStats(
    selectedAccountId || undefined,
    undefined,
    currentRange
  );
  const { data: previousContactStats } = useContactStats(
    selectedAccountId || undefined,
    undefined,
    previousRange
  );
  const { data: adsBalanceData } = useAdsBalance(adsConnected);

  const pendingTemplates = pendingOnboardingAssets?.pending_templates ?? [];
  const pendingFlow = pendingOnboardingAssets?.pending_flow ?? null;
  const activatedBusinessAccountId = pendingOnboardingAssets?.activated_business_account_id ?? null;

  // Sourced from server-side /analytics/campaigns aggregate, NOT a client-side
  // reduce over a paginated useCampaigns page (which would silently undercount
  // any org with > pageSize campaigns).
  const campaignStats = useMemo(() => {
    const a = campaignAnalytics as any;
    if (!a) {
      return { totalCampaigns: 0, totalMessagesSent: 0, totalMessagesDelivered: 0, totalMessagesRead: 0, readRate: 0 };
    }
    const totalCampaigns = Number(a.totalCampaigns || 0);
    const totalMessagesSent = Number(a.totalSent || 0);
    const totalMessagesDelivered = Number(a.totalDelivered || 0);
    const totalMessagesRead = Number(a.totalRead || 0);
    let readRate = 0;
    if (totalMessagesDelivered > 0) readRate = Math.round((totalMessagesRead / totalMessagesDelivered) * 100);
    else if (totalMessagesSent > 0) readRate = Math.round((totalMessagesRead / totalMessagesSent) * 100);
    return { totalCampaigns, totalMessagesSent, totalMessagesDelivered, totalMessagesRead, readRate };
  }, [campaignAnalytics]);

  // Messaging limit progress (must be before early returns — Rules of Hooks)
  const msgLimitPct = useMemo(() => {
    if (!messagingUsageInfo || messagingUsageInfo.isUnlimited) return null;
    const limit = messagingUsageInfo.limit ?? 0;
    const used = messagingUsageInfo.used ?? 0;
    if (limit <= 0) return null;
    return Math.min((used / limit) * 100, 100);
  }, [messagingUsageInfo]);

  // Demo popup state (must be before early returns — Rules of Hooks)
  // Demo booking modal — opens ONLY when the user clicks "Book a 20-min demo".
  // (Previously auto-opened on every Overview visit — removed.)
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const [isConnectDialogOpen, setIsConnectDialogOpen] = useState(false);

  // ── "SETUP PROGRESS" onboarding card (3 steps) ──
  // Defined BEFORE the early returns so the no-account Overview (empty state)
  // and the populated dashboard both render the same card. Each step's `done`
  // is derived from live data: Meta connected = an account with a token/BA id;
  // contacts added = totalContacts > 0; launched = ≥1 campaign OR automation.
  const setupContactsCount = (contactStatsData as any)?.totalContacts ?? 0;
  const setupSteps: SetupStep[] = [
    {
      title: 'Connect your Meta account',
      done: !!isAccountConnected,
      ctas: [{ label: 'Connect', onClick: () => setIsConnectDialogOpen(true) }],
    },
    {
      title: 'Add your first contacts',
      done: setupContactsCount > 0,
      ctas: [{ label: 'Add contacts', onClick: () => navigate('/home/leads') }],
    },
    {
      title: 'Launch a campaign or automation',
      done: campaignStats.totalCampaigns > 0 || automationConnected,
      ctas: [
        { label: 'Campaign', onClick: () => navigate('/home/campaigns') },
        { label: 'Automation', onClick: () => navigate('/home/automation') },
      ],
    },
  ];

  // ── Loading State ──
  if (accountsLoading || pendingAssetsLoading) {
    return (
      <div className="bg-[#F5F7F9] min-h-screen px-[30px] py-[32px] pt-[100px] flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ x: [-15, 15, -15], rotate: [-5, 5, -5] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="mb-6"
          >
            <img src={rabbit_icon} alt="Loading..." className="h-28 w-28 mx-auto" />
          </motion.div>
          <h3 className="text-xl font-bold mb-2 tracking-tight text-[#08090A]">Loading Dashboard</h3>
          <p className="text-muted-foreground animate-pulse">Please wait while we load your business accounts...</p>
        </div>
      </div>
    );
  }

  // ── No Account Selected ──
  if (!selectedAccountId) {
    const hasAccounts = (accounts && accounts.length > 0) || (businessAccounts && businessAccounts.length > 0);
    const hasPendingAssets = pendingTemplates.length > 0 || !!pendingFlow || !!activatedBusinessAccountId;
    return (
      <div className="bg-[#F5F7F9] min-h-screen px-[30px] py-[32px] pt-[20px]">
        <div className="space-y-8 animate-fade-in">
          {/* Onboarding cards — centered with generous left/right gutters.
              (The "Overview / View your dashboard statistics…" header is
              intentionally hidden on this empty state.) */}
          <div className="space-y-6 mx-auto w-full max-w-[980px] xl:px-4">
            {/* SETUP PROGRESS onboarding checklist (steps tick as the user
                completes them), paired with a "Book a demo" walkthrough card. */}
            <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
              <SetupProgress steps={setupSteps} className="min-w-0 lg:flex-1" />
              <aside className="relative flex flex-col overflow-hidden rounded-2xl bg-[linear-gradient(135deg,#D8F2E2_0%,#EAF9F0_12%,#FFFFFF_38%)] p-6 lg:w-[300px] lg:shrink-0">
                {/* Decorative dot grid — dense at top, fading out toward the bottom. */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0"
                  style={{
                    backgroundImage:
                      'radial-gradient(circle, rgba(14,182,79,0.18) 1.2px, transparent 1.2px)',
                    backgroundSize: '14px 14px',
                    WebkitMaskImage:
                      'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 70%)',
                    maskImage:
                      'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 70%)',
                  }}
                />

                {/* Icon on the left, "Guided" chip pushed to the opposite corner. */}
                <div className="relative z-10 flex items-center justify-between gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm text-[#0EB64F]">
                    <Calendar className="h-5 w-5" />
                  </div>
                  <span className="inline-flex items-center rounded-full bg-[#E7F7EC] px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#0EB64F]">
                    Guided
                  </span>
                </div>

                <h3 className="relative z-10 mt-4 text-[18px] font-bold text-[#101828]">Need help getting started?</h3>
                <p className="relative z-10 mt-2 text-[13.5px] leading-relaxed text-[#6B7280]">
                  Book a 20-minute setup call. We&apos;ll connect your channels, configure your workspace, and help you launch your first AI automation.
                </p>
                <button
                  type="button"
                  onClick={() => setIsDemoOpen(true)}
                  className="relative z-10 mt-4 flex items-center justify-center gap-2 rounded-xl border border-[#E4E7EC] bg-white px-4 py-2.5 text-[14px] font-semibold text-[#101828] hover:bg-[#F9FAFB]"
                >
                  Book a Setup Call
                </button>
              </aside>
            </div>
            {/* "What you can do with WeNext" hero removed from Overview — the
                Meta banner + Setup Progress card above now serve that role here.
                The shared ConnectBusinessAccountState component is untouched and
                still used on every other page/tab. */}
            {(hasAccounts || hasPendingAssets) && (
              <Card className="border border-[#E2E8F0]">
                <CardContent className="p-8 text-center">
                  <Building2 className="h-14 w-14 mx-auto text-[#94A3B8] mb-4" />
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2 tracking-tight">
                    {hasPendingAssets ? 'Onboarding Assets Ready' : 'No Business Account Selected'}
                  </h3>
                  <p className="text-[14px] text-[#475569] mb-5 max-w-md mx-auto leading-relaxed">
                    {hasPendingAssets
                      ? 'Your generated templates and automation flow are ready. Open them directly below.'
                      : 'Please select a business account to view your dashboard.'}
                  </p>
                  {hasPendingAssets ? (
                    <div className="flex flex-col items-center gap-3">
                      <div className="flex items-center justify-center gap-3">
                        {pendingTemplates.length > 0 && (
                          <Button size="sm" onClick={() => navigate('/home/templates')}>
                            View Templates
                          </Button>
                        )}
                        {(pendingFlow || activatedBusinessAccountId) && (
                          <Button size="sm" variant="outline" onClick={() => navigate('/home/automation/flows')}>
                            View Automation
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate('/home/business-accounts')}
                        >
                          Connect Business Account
                        </Button>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => window.open('https://app.wenext.ai/book/wenext-platform-walkthrough', '_blank')}>
                        Schedule a Demo
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <p className="text-[13px] text-[#64748B] font-medium">Use the business account selector in the header to choose an account.</p>
                      <Button size="sm" variant="outline" onClick={() => window.open('https://app.wenext.ai/book/wenext-platform-walkthrough', '_blank')}>
                        Schedule a Demo
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Mobile app card + workspace feature grid — ported from the
                wenext-onboarding design, below the Setup Progress card. */}
            <MobileAppCard
              appStoreUrl="https://apps.apple.com/in/app/wenext/id6760423918"
              googlePlayUrl="https://play.google.com/store/apps/details?id=ai.wenext.app"
              // QR encodes the device-routing landing so one code serves both
              // stores (iOS → App Store, Android → Play Store).
              qrUrl={`${typeof window !== 'undefined' ? window.location.origin : ''}/get-app`}
            />
            <WorkspaceFeatures />

          </div>
        </div>

        {/* Connect-account flow — "Connect once, activate all" modal, launches
            Meta's real embedded signup on "Continue with Meta". */}
        <ConnectMetaModal
          open={isConnectDialogOpen}
          onClose={() => setIsConnectDialogOpen(false)}
        />

        {/* Demo booking popup — opens only via the "Book a 20-min demo" link */}
        <Dialog open={isDemoOpen} onOpenChange={setIsDemoOpen}>
          <DialogContent className="flex h-[90dvh] max-h-[90dvh] w-[calc(100%-1.5rem)] max-w-5xl flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:w-[90vw] sm:rounded-2xl">
            <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
              <h3 className="text-base font-semibold text-[#0F172A]">Schedule a Demo</h3>
            </div>
            <DemoBookingEmbed src="https://app.wenext.ai/book/wenext-platform-walkthrough" />
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ── Computed Values ──
  const roleRaw = (user?.role || '').toString().toUpperCase();
  const isSuper = !!(user as any)?.is_super_admin;
  const isOwner = !!(user as any)?.is_organization_owner;
  const isAdmin = roleRaw === 'ADMIN' || isSuper || isOwner;

  const allServices = [
    { label: 'WhatsApp', connected: !!isAccountConnected, icon: Phone, path: '/home/business-accounts' },
    { label: 'Templates', connected: templatesConnected, icon: LayoutTemplate, path: '/home/templates' },
    { label: 'Campaigns', connected: campaignStats.totalCampaigns > 0, icon: Send, path: '/home/campaigns' },
    // { label: 'Meta Ads', connected: adsConnected, icon: Megaphone, path: '/home/ads' },
    { label: 'Commerce', connected: commerceConnected, icon: ShoppingCart, path: '/home/commerce/dashboard' },
    { label: 'Automation', connected: automationConnected, icon: Zap, path: '/home/automation' },
    { label: 'Live Chat', connected: liveChatConnected, icon: MessageCircle, path: '/home/live-chat' },
    { label: 'Scheduling', connected: schedulingConnected, icon: CalendarCheck, path: '/home/scheduling' },
    { label: 'Payments', connected: !!razorpayConnected, icon: CreditCard, path: '/home/integrations' },
    { label: 'AI Agents', connected: aiAgentsConnected, icon: Bot, path: '/home/ai-agents' },
    { label: 'WA Flows', connected: whatsappFlowsConnected, icon: Workflow, path: '/home/flows' },
  ];
  const activeServicesCount = allServices.filter(s => s.connected).length;

  const totalContacts = (contactStatsData as any)?.totalContacts ?? stats?.contacts ?? 0;
  const totalMessages = (overviewAnalytics as any)?.totalMessages ?? stats?.messages ?? 0;
  const activeChats = (overviewAnalytics as any)?.activeConversations ?? stats?.conversations ?? 0;
  const deliveryRate = (overviewAnalytics as any)?.deliveryRate;
  const readRate = (overviewAnalytics as any)?.readRate;
  const newContactsToday = (contactStatsData as any)?.newContactsToday ?? 0;
  // Contacts created within the selected range (falls back to today's count if the
  // backend didn't return a range value, e.g. older API build).
  const newContactsInRange = (contactStatsData as any)?.newContactsInRange ?? newContactsToday;
  const prevNewContactsInRange = (previousContactStats as any)?.newContactsInRange ?? null;

  // Onboarding checklist
  const onboardingSteps = [
    { label: 'Connect WhatsApp', done: !!isAccountConnected, path: '/home/business-accounts' },
    { label: 'Create a template', done: templatesConnected, path: '/home/templates' },
    { label: 'Send first campaign', done: campaignStats.totalCampaigns > 0, path: '/home/campaigns' },
    { label: 'Add team member', done: teamCount > 1, path: '/home/team-management' },
    { label: 'Set up automation', done: automationConnected, path: '/home/automation' },
  ];
  const completedSteps = onboardingSteps.filter(s => s.done).length;
  const allOnboardingDone = completedSteps === onboardingSteps.length;

  // Alerts/anomalies
  const alerts: { id: string; type: 'warning' | 'info' | 'error'; title: string; description: string }[] = [];
  if (accountStatus === 'PENDING') {
    alerts.push({ id: 'pending-account', type: 'warning', title: 'Account Verification Pending', description: 'Your WhatsApp Business account is pending verification by Meta. Some features may be limited.' });
  }
  if (quality === 'RED' || quality === 'LOW' || quality === 'Low') {
    alerts.push({ id: 'low-quality', type: 'error', title: 'Low Quality Rating', description: 'Your WhatsApp quality rating is low. This may limit your messaging capacity. Review your message templates and sending practices.' });
  }
  if (messagingUsageInfo && !messagingUsageInfo.isUnlimited) {
    const limit = messagingUsageInfo.limit ?? 0;
    const used = messagingUsageInfo.used ?? 0;
    if (limit > 0 && used / limit > 0.8) {
      alerts.push({ id: 'msg-limit', type: 'warning', title: 'Messaging Limit Almost Reached', description: `You've used ${Math.round((used / limit) * 100)}% of your daily messaging limit (${formatNumber(used)} / ${formatNumber(limit)}).` });
    }
  }
  const visibleAlerts = alerts.filter(a => !dismissedAlerts.has(a.id));

  const firstName = user?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'there';

  // ── Render ──
  return (
    <div className="bg-[#F8F9FB] min-h-screen px-3 sm:px-4 md:px-6 lg:px-[30px] pb-8 pt-3 sm:pt-4 md:pt-6 lg:pt-[12px]">
      <div className="space-y-5 animate-fade-in max-w-[1600px] mx-auto">

        {/* ═══════════════════════════════════════════════════
            1. PERSONALIZATION & HEADER
        ═══════════════════════════════════════════════════ */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[26px] md:text-[28px] lg:text-[30px] font-bold text-[#0F172A] tracking-tight leading-tight break-words">
              {getGreeting()}, {firstName}
            </h1>
            <p className="text-[13px] sm:text-[14px] text-[#475569] mt-1.5 flex items-center gap-1.5 font-medium">
              <Clock className="h-3.5 w-3.5 text-[#64748B] shrink-0" />
              {getTimestamp()}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className="h-9 text-[13px] font-semibold rounded-lg border-[#CBD5E1] text-[#334155] hover:bg-[#F1F5F9] hover:border-[#94A3B8]"
              onClick={() => navigate('/home/analytics')}
            >
              <BarChart3 className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Reports</span>
            </Button>
            <Button
              size="sm"
              className="h-9 text-[13px] rounded-lg font-semibold text-white shadow-sm"
              style={{ backgroundColor: 'var(--secondary-color)' }}
              onClick={() => navigate('/home/campaigns')}
            >
              <Plus className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">New Campaign</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════
            SETUP PROGRESS — onboarding checklist, shown ONLY until the account
            is connected (hidden for connected users; steps still tick live).
        ═══════════════════════════════════════════════════ */}
        {!isAccountConnected && <SetupProgress steps={setupSteps} />}

        {/* ═══════════════════════════════════════════════════
            14. QUICK ACTIONS STRIP
        ═══════════════════════════════════════════════════ */}
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'Send Campaign', icon: Send, path: '/home/campaigns', color: '#8B5CF6' },
            { label: 'Create Template', icon: LayoutTemplate, path: '/home/templates', color: '#14B8A6' },
            { label: 'View Inbox', icon: MessageSquare, path: '/home/inbox', color: '#3B82F6' },
            { label: 'View Customers', icon: Users, path: '/home/customers', color: '#06B6D4' },
            // { label: 'Run Ads', icon: Megaphone, path: adsConnected ? '/home/ads' : '/home/ads/setup', color: '#F59E0B' },
          ].map((action) => (
            <button
              key={action.label}
              onClick={() => navigate(action.path)}
              className="flex items-center gap-2 px-3.5 py-2 rounded-full text-[13px] font-semibold bg-white border border-[#CBD5E1] text-[#334155] hover:border-[#64748B] hover:shadow-sm transition-all"
            >
              <action.icon className="h-3.5 w-3.5" style={{ color: action.color }} />
              {action.label}
            </button>
          ))}
        </div>

        {/* ═══════════════════════════════════════════════════
            2. ALERT & ANOMALY SYSTEM
        ═══════════════════════════════════════════════════ */}
        {visibleAlerts.length > 0 && (
          <div className="space-y-2">
            {visibleAlerts.map((alert) => (
              <Alert
                key={alert.id}
                className={`relative pr-10 ${
                  alert.type === 'error' ? 'border-red-200 bg-red-50/80' :
                  alert.type === 'warning' ? 'border-amber-200 bg-amber-50/80' :
                  'border-blue-200 bg-blue-50/80'
                }`}
              >
                <AlertTriangle className={`h-4 w-4 ${
                  alert.type === 'error' ? 'text-red-600' :
                  alert.type === 'warning' ? 'text-amber-600' :
                  'text-blue-600'
                }`} />
                <AlertTitle className={`text-[14px] font-bold ${
                  alert.type === 'error' ? 'text-red-900' :
                  alert.type === 'warning' ? 'text-amber-900' :
                  'text-blue-900'
                }`}>{alert.title}</AlertTitle>
                <AlertDescription className={`mt-1 text-[13px] leading-relaxed ${
                  alert.type === 'error' ? 'text-red-800' :
                  alert.type === 'warning' ? 'text-amber-800' :
                  'text-blue-800'
                }`}>
                  {alert.description}
                </AlertDescription>
                <button
                  onClick={() => setDismissedAlerts(prev => new Set([...prev, alert.id]))}
                  className="absolute top-3 right-3 p-1 rounded-md hover:bg-black/10 transition-colors"
                >
                  <X className="h-4 w-4 text-[#475569]" />
                </button>
              </Alert>
            ))}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════
            SECTION: PLATFORM HEALTH
        ═══════════════════════════════════════════════════ */}
        {user.role !== "user" && (
          <>
            {/* WhatsApp Status + Messaging Limit Bar */}
            <div className="rounded-xl px-4 sm:px-5 py-3 flex flex-wrap items-center gap-x-3 sm:gap-x-5 gap-y-2 shadow-sm" style={{ background: 'linear-gradient(135deg, #075E54 0%, #128C7E 50%, #25D366 100%)' }}>
              <div className="flex items-center gap-2.5">
                <img src={whatsapp_icon} alt="WhatsApp" className="w-7 h-7" />
                <span className="text-[15px] font-bold text-white tracking-wide">{phoneNumber || '—'}</span>
                <Badge className={`${accountStatus === 'PENDING' ? 'bg-amber-400/95' : 'bg-white/25'} text-white text-[11px] font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1.5 border-0`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${accountStatus === 'PENDING' ? 'bg-amber-100' : 'bg-emerald-300'} animate-pulse`} />
                  {accountStatus === 'PENDING' ? 'Pending' : 'Live'}
                </Badge>
              </div>
              {isAccountConnected && (
                <>
                  {/* Messaging Limit Progress */}
                  {messagingUsageInfo && (
                    <>
                      <div className="h-5 w-px bg-white/25 hidden sm:block" />
                      <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-[380px]">
                        <span className="text-[12px] font-semibold text-white/90 whitespace-nowrap flex items-center gap-1.5">
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-400" />
                          </span>
                          Msg Limit:
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  aria-label="What does the messaging limit mean?"
                                  className="inline-flex items-center justify-center text-white/70 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-full"
                                >
                                  <Info className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" align="start" className="w-[260px] max-w-[calc(100vw-24px)] whitespace-normal break-words text-[11px] leading-relaxed">
                                <p className="font-semibold mb-1">New customers you can message per day</p>
                                <p>
                                  {messagingUsageInfo.isUnlimited
                                    ? 'You can start conversations with an unlimited number of new customers each day.'
                                    : messagingUsageInfo.limit != null && messagingUsageInfo.limit > 0
                                      ? <>You can start conversations with up to <strong>{formatNumber(messagingUsageInfo.limit)} new customers per day</strong>.</>
                                      : 'This is how many new customers you can start a conversation with each day.'}
                                </p>
                                <p className="mt-1 text-muted-foreground">
                                  Counts only messages where <strong>you reach out first</strong> (template / marketing / utility) and open a new 24-hour chat — on a rolling 24h window. Replies inside an open chat, and conversations a customer starts, don't count.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </span>
                        {msgLimitPct !== null ? (
                          <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-700"
                              style={{
                                width: `${msgLimitPct}%`,
                                backgroundColor: msgLimitPct > 80 ? '#EF4444' : msgLimitPct > 60 ? '#F59E0B' : '#86EFAC',
                              }}
                            />
                          </div>
                        ) : !messagingUsageInfo.isUnlimited ? (
                          <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden" />
                        ) : null}
                        <span className="text-[12px] font-bold text-white whitespace-nowrap">
                          {messagingUsageInfo.isUnlimited
                            ? 'Unlimited'
                            : messagingUsageInfo.limit != null && messagingUsageInfo.limit > 0
                              ? `${formatNumber(messagingUsageInfo.used ?? 0)}/${formatNumber(messagingUsageInfo.limit)}`
                              : messagingUsageInfo.tier
                                ? (messagingUsageInfo.tier || '').replace('TIER_', 'Tier ')
                                : 'Not Assigned'}
                        </span>
                        {isUsageRefetching && (
                          <Loader2 className="h-3 w-3 animate-spin text-white/70" />
                        )}
                      </div>
                    </>
                  )}
                  <div className="h-5 w-px bg-white/25 hidden sm:block" />
                  <div className="flex items-center gap-1.5">
                    <span className="text-[12px] font-semibold text-white/90">Quality:</span>
                    {isLoadingUsage ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-white/80" />
                    ) : quality && quality !== 'UNKNOWN' ? (
                      <Badge className={`text-[11px] px-2 py-0.5 rounded-full font-bold border-0 ${quality === 'RED' || quality === 'LOW' || quality === 'Low' ? 'bg-red-500 text-white' : quality === 'YELLOW' || quality === 'MEDIUM' || quality === 'Medium' ? 'bg-amber-400 text-white' : 'bg-white/25 text-white'}`}>
                        {quality}
                      </Badge>
                    ) : (
                      <span className="text-[12px] text-white/80">—</span>
                    )}
                  </div>
                </>
              )}
              <div className="ml-auto flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-3.5 text-[12px] font-bold rounded-lg bg-white/20 hover:bg-white/30 text-white border-0"
                  onClick={() => navigate('/home/business-accounts')}
                >
                  {isAccountConnected ? 'View Profile' : 'Select Account'}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full bg-white/15 hover:bg-white/30 text-white"
                  onClick={() => refetchUsage()}
                  disabled={isLoadingUsage || isUsageRefetching}
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isUsageRefetching ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {/* AI Lead Insight */}
            {selectedAccountId && (
              <AiInsightCard
                insight={(stats?.aiInsight as any) ?? {
                  analysisDate: null,
                  status: null,
                  summary: null,
                  aiInsights: [],
                  metrics: null,
                  error: null,
                  triggeredAt: null,
                  updatedAt: null,
                }}
              />
            )}

            {/* Platform Health - Services Status Chips */}
            {isAdmin && (
              <Card className="border border-[#E2E8F0] shadow-sm bg-white rounded-xl">
                <CardContent className="p-0">
                  <div className="px-5 pt-4 pb-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#F0FDF4] flex items-center justify-center">
                        <Shield className="h-4.5 w-4.5 text-[#15803D]" />
                      </div>
                      <div>
                        <h3 className="text-[16px] font-bold text-[#0F172A] tracking-tight">Platform Health</h3>
                        <p className="text-[12px] text-[#64748B] font-medium mt-0.5">{activeServicesCount} of {allServices.length} services active</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <div className="w-28 h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${(activeServicesCount / allServices.length) * 100}%`,
                            backgroundColor: activeServicesCount / allServices.length > 0.7 ? '#22C55E' : activeServicesCount / allServices.length > 0.4 ? '#F59E0B' : '#EF4444',
                          }}
                        />
                      </div>
                      <span className="text-[15px] font-bold text-[#0F172A]">{Math.round((activeServicesCount / allServices.length) * 100)}%</span>
                    </div>
                  </div>
                  <div className="px-5 pb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                    {allServices.map((svc) => (
                      <button
                        key={svc.label}
                        onClick={() => navigate(svc.path)}
                        className={`w-full flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[12px] font-semibold border transition-all hover:shadow-sm cursor-pointer ${
                          svc.connected
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100'
                            : 'bg-[#F8FAFC] border-[#CBD5E1] text-[#64748B] hover:border-[#94A3B8]'
                        }`}
                      >
                        <svc.icon className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{svc.label}</span>
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${svc.connected ? 'bg-emerald-500' : 'bg-[#94A3B8]'}`} />
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════════════════
            3. HERO METRIC + 4. STAT CARDS (with sparklines & trends)
            SECTION: ACQUISITION
        ═══════════════════════════════════════════════════ */}
        <div>
          <div className="flex flex-wrap items-end justify-between gap-2 mb-2">
            <SectionHeader label="Acquisition" color="#3B82F6" subtitle={`Messages, contacts & reach (${rangeLabel.toLowerCase()})`} />
            <div className="inline-flex items-center rounded-lg border border-[#CBD5E1] bg-white p-0.5">
              {DATE_RANGE_PRESETS.map(p => (
                <button
                  key={p.key}
                  onClick={() => setRangeKey(p.key)}
                  className={`px-3 py-1.5 text-[12px] font-semibold rounded-md transition-colors ${
                    rangeKey === p.key
                      ? 'bg-[#0F172A] text-white'
                      : 'text-[#475569] hover:text-[#0F172A]'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {(() => {
              const prev = (previousOverview as any) || {};
              const messagesDelta = computeDelta(totalMessages, Number(prev.totalMessages ?? 0));
              const chatsDelta = computeDelta(activeChats, Number(prev.activeConversations ?? 0));
              const deliveryDelta = computeDelta(
                deliveryRate != null ? Number(deliveryRate) : null,
                prev.deliveryRate != null ? Number(prev.deliveryRate) : null,
              );
              const readDelta = computeDelta(
                readRate != null ? Number(readRate) : null,
                prev.readRate != null ? Number(prev.readRate) : null,
              );
              const newContactsDelta = prevNewContactsInRange != null
                ? computeDelta(Number(newContactsInRange), Number(prevNewContactsInRange))
                : null;
              const kpis: { label: string; value: number | string | null; icon: any; color: string; delta: number | null; clickPath?: string }[] = [
                { label: 'Total Messages', value: analyticsLoading ? null : totalMessages, icon: MessageSquare, color: '#3B82F6', delta: messagesDelta, clickPath: '/home/analytics' },
                { label: 'Active Chats', value: analyticsLoading ? null : activeChats, icon: MessageCircle, color: '#8B5CF6', delta: chatsDelta, clickPath: '/home/inbox' },
                // Total Contacts is scoped to the selected range (contacts acquired in the window)
                // and trended vs the previous equal-length window.
                { label: 'Total Contacts', value: contactStatsLoading ? null : newContactsInRange, icon: Users, color: '#06B6D4', delta: newContactsDelta, clickPath: '/home/customers' },
                { label: 'Delivery Rate', value: analyticsLoading ? null : deliveryRate != null ? `${Number(deliveryRate).toFixed(1)}%` : '—', icon: CheckCircle, color: '#10B981', delta: deliveryDelta },
                { label: 'Read Rate', value: analyticsLoading ? null : readRate != null ? `${Number(readRate).toFixed(1)}%` : '—', icon: Eye, color: '#F59E0B', delta: readDelta },
                // New Today is today-only (distinct from the range-scoped Total Contacts above).
                { label: 'New Today', value: contactStatsLoading ? null : newContactsToday, icon: Activity, color: '#EC4899', delta: null, clickPath: '/home/customers' },
              ];
              return kpis.map((kpi) => (
                <Card
                  key={kpi.label}
                  className="border border-[#E2E8F0] shadow-sm bg-white rounded-xl hover:shadow-md hover:border-[#CBD5E1] transition-all cursor-pointer"
                  onClick={() => kpi.clickPath && navigate(kpi.clickPath)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${kpi.color}18` }}>
                        <kpi.icon className="h-4.5 w-4.5" style={{ color: kpi.color }} />
                      </div>
                      {kpi.delta != null && kpi.value !== null && Math.abs(kpi.delta) >= 0.1 && (
                        <span className={`text-[12px] font-bold flex items-center gap-0.5 px-1.5 py-0.5 rounded-md ${kpi.delta > 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-600 bg-red-50'}`}>
                          {kpi.delta > 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                          {Math.abs(kpi.delta).toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <p className="text-[28px] font-bold text-[#0F172A] leading-none tracking-tight">
                      {kpi.value === null ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}
                    </p>
                    <p className="text-[13px] text-[#475569] mt-1.5 font-semibold">{kpi.label}</p>
                  </CardContent>
                </Card>
              ));
            })()}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════
            6. ONBOARDING & SETUP CHECKLIST
        ═══════════════════════════════════════════════════ */}
        {isAdmin && !allOnboardingDone && (
          <Card className="border border-[#E2E8F0] shadow-sm bg-gradient-to-r from-[#F8FAFF] to-white rounded-xl overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#EEF2FF] flex items-center justify-center">
                    <Rocket className="h-4.5 w-4.5 text-[#6366F1]" />
                  </div>
                  <div>
                    <h3 className="text-[16px] font-bold text-[#0F172A] tracking-tight">Getting Started</h3>
                    <p className="text-[12px] text-[#64748B] font-medium mt-0.5">{completedSteps} of {onboardingSteps.length} completed</p>
                  </div>
                </div>
                <span className="text-[18px] font-bold text-[#6366F1]">{Math.round((completedSteps / onboardingSteps.length) * 100)}%</span>
              </div>
              <div className="w-full h-2 bg-[#F1F5F9] rounded-full overflow-hidden mb-4">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${(completedSteps / onboardingSteps.length) * 100}%`, background: 'linear-gradient(90deg, #6366F1, #8B5CF6)' }}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                {onboardingSteps.map((step) => (
                  <button
                    key={step.label}
                    onClick={() => !step.done && navigate(step.path)}
                    className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-left transition-all ${
                      step.done
                        ? 'bg-emerald-50 cursor-default'
                        : 'bg-white border border-[#CBD5E1] hover:border-[#6366F1] hover:shadow-sm cursor-pointer'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      step.done ? 'bg-emerald-500' : 'border-2 border-[#94A3B8]'
                    }`}>
                      {step.done && <CheckCircle className="h-3.5 w-3.5 text-white" />}
                    </div>
                    <span className={`text-[13px] font-semibold ${step.done ? 'text-emerald-700 line-through' : 'text-[#334155]'}`}>
                      {step.label}
                    </span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ═══════════════════════════════════════════════════
            SECTION: ENGAGEMENT
            8. CAMPAIGNS FUNNEL + Message Breakdown
        ═══════════════════════════════════════════════════ */}
        <div>
          <SectionHeader label="Engagement" color="#8B5CF6" subtitle="Campaign performance & messaging" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

            {/* Campaigns Funnel */}
            <Card className="border border-[#E2E8F0] shadow-sm bg-white rounded-xl">
              <CardHeader className="pb-3 pt-4 px-5">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-[16px] font-bold text-[#0F172A] flex items-center gap-2 tracking-tight">
                    <Target className="h-4.5 w-4.5 text-[#8B5CF6]" />
                    Campaign Funnel
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="h-7 text-[12px] font-semibold text-[#475569] hover:text-[#0F172A]" onClick={() => navigate('/home/campaigns')}>
                    View All <ChevronRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                {campaignAnalyticsLoading ? (
                  <div className="flex items-center justify-center h-[140px]"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                ) : campaignStats.totalCampaigns === 0 ? (
                  <EmptyState
                    icon={Send}
                    title="No campaigns yet"
                    description="Send your first WhatsApp campaign to see funnel analytics here."
                    actionLabel="Create Campaign"
                    onAction={() => navigate('/home/campaigns')}
                  />
                ) : (
                  <div className="space-y-3">
                    {[
                      { label: 'Campaigns Created', value: campaignStats.totalCampaigns, color: '#8B5CF6', width: 100 },
                      // Messages Sent is the baseline of the messaging funnel —
                      // a full bar when any messages were sent (Delivered/Read
                      // below are real ratios of it). No fabricated 90%.
                      { label: 'Messages Sent', value: campaignStats.totalMessagesSent, color: '#3B82F6', width: campaignStats.totalMessagesSent > 0 ? 100 : 0 },
                      { label: 'Delivered', value: campaignStats.totalMessagesDelivered, color: '#10B981', width: campaignStats.totalMessagesSent > 0 ? Math.round((campaignStats.totalMessagesDelivered / campaignStats.totalMessagesSent) * 100) : 0 },
                      { label: 'Read', value: campaignStats.totalMessagesRead, color: '#F59E0B', width: campaignStats.totalMessagesSent > 0 ? Math.round((campaignStats.totalMessagesRead / campaignStats.totalMessagesSent) * 100) : 0 },
                    ].map((step) => (
                      <div key={step.label}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[13px] font-semibold text-[#334155]">{step.label}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[15px] font-bold text-[#0F172A]">{step.value.toLocaleString()}</span>
                            {step.width < 100 && step.width > 0 && (
                              <span className="text-[12px] font-semibold text-[#64748B]">{step.width}%</span>
                            )}
                          </div>
                        </div>
                        <div className="w-full h-2.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${step.width}%`, backgroundColor: step.color }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Message Breakdown — non-overlapping buckets that add to Sent.
                Background: the legacy version listed Sent/Delivered/Read as
                separate progress bars, which read like a pie chart — users
                tried to subtract them ("179 sent − 160 delivered − 102 read =
                ?") and got confused. The reality is Read ⊂ Delivered ⊂ Sent.
                This redesign splits the funnel into disjoint segments
                (Read | Delivered-but-not-read | Pending | Failed) that
                actually sum to Sent, plus a single stacked bar showing the
                proportion at a glance. Inbound is shown separately because
                it's orthogonal to the outbound funnel. */}
            <Card className="border border-[#E2E8F0] shadow-sm bg-white rounded-xl">
              <CardHeader className="pb-3 pt-4 px-5">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-[16px] font-bold text-[#0F172A] flex items-center gap-2 tracking-tight">
                    <BarChart3 className="h-4.5 w-4.5 text-[#3B82F6]" />
                    Message Delivery Insights
                  </CardTitle>
                  <span className="text-[12px] font-semibold text-[#64748B] bg-[#F1F5F9] px-2.5 py-1 rounded-full">{rangeLabel}</span>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                {analyticsLoading ? (
                  <div className="flex items-center justify-center h-[140px]"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                ) : (() => {
                  const breakdown = (overviewAnalytics as any)?.messageBreakdown;
                  const totalSent = Number(breakdown?.totalSent ?? breakdown?.sent ?? campaignStats.totalMessagesSent ?? 0);
                  const totalDelivered = Number(breakdown?.totalDelivered ?? breakdown?.delivered ?? campaignStats.totalMessagesDelivered ?? 0);
                  const totalRead = Number(breakdown?.totalRead ?? breakdown?.read ?? campaignStats.totalMessagesRead ?? 0);
                  const totalFailed = Number(breakdown?.totalFailed ?? breakdown?.failed ?? 0);
                  const totalReceived = Number(breakdown?.totalReceived ?? breakdown?.received ?? 0);

                  // Derive disjoint segments. Clamp at 0 so any backend
                  // inconsistency (e.g. read > delivered from a race) never
                  // surfaces a negative number on screen.
                  const readCount = Math.max(0, totalRead);
                  const deliveredOnly = Math.max(0, totalDelivered - totalRead);
                  const pending = Math.max(0, totalSent - totalDelivered - totalFailed);
                  const failed = Math.max(0, totalFailed);

                  const allZero = totalSent === 0 && totalReceived === 0;
                  if (allZero) {
                    return (
                      <EmptyState
                        icon={MessageSquare}
                        title="No messages yet"
                        description="Start sending messages to see your breakdown analytics."
                        actionLabel="Go to Inbox"
                        onAction={() => navigate('/home/inbox')}
                      />
                    );
                  }

                  const segments = [
                    { key: 'read', label: 'Read', value: readCount, color: '#F59E0B', hint: 'Customer opened it' },
                    { key: 'delivered_only', label: 'Unread', value: deliveredOnly, color: '#10B981', hint: 'Delivered but not opened yet' },
                    {
                      key: 'pending', label: 'Pending', value: pending, color: '#94A3B8', hint: 'Still on the way',
                      helpTitle: 'Why is a message pending?',
                      helpIntro: "WhatsApp accepted the message, but the delivery confirmation hasn't come back yet. Usually one of:",
                      helpReasons: [
                        "Recipient's phone is off or offline",
                        "They haven't opened WhatsApp in a while",
                        "The number isn't on WhatsApp",
                        "They blocked your business",
                        "WhatsApp is still processing — give it a few minutes",
                      ],
                    },
                    {
                      key: 'failed', label: 'Failed', value: failed, color: '#EF4444', hint: "Couldn't be delivered",
                      helpTitle: 'Why did a message fail?',
                      helpIntro: "WhatsApp rejected the message. The most common reasons:",
                      helpReasons: [
                        "Outside the 24-hour window — customer hasn't replied in 24h, so only an approved template can be sent",
                        "Template not approved, paused, or removed by Meta",
                        "Phone number isn't on WhatsApp or is invalid",
                        "Customer opted out or blocked your business",
                        "Daily messaging limit hit — wait until midnight UTC or upgrade your tier",
                        "Media too large or wrong format (images > 5 MB, videos > 16 MB)",
                      ],
                    },
                  ];
                  const denom = Math.max(1, totalSent);
                  const pctOf = (v: number) => (v / denom) * 100;

                  return (
                    <div className="space-y-3">
                      {/* Headline — Sent is the universe */}
                      <div className="flex items-baseline justify-between border-b border-[#F1F5F9] pb-2">
                        <div className="text-[12px] font-semibold text-[#64748B] uppercase tracking-wide">Sent</div>
                        <div className="text-[22px] font-bold text-[#0F172A] tracking-tight">{totalSent.toLocaleString()}</div>
                      </div>

                      {/* Single stacked bar — at-a-glance proportion */}
                      {totalSent > 0 && (
                        <div className="flex w-full h-3 bg-[#F1F5F9] rounded-full overflow-hidden" title="Of every message sent, the colored segments show its final state">
                          {segments.map(seg => seg.value > 0 && (
                            <div
                              key={seg.key}
                              className="h-full transition-all duration-700"
                              style={{ width: `${pctOf(seg.value)}%`, backgroundColor: seg.color }}
                              title={`${seg.label}: ${seg.value.toLocaleString()} (${pctOf(seg.value).toFixed(1)}%)`}
                            />
                          ))}
                        </div>
                      )}

                      {/* Per-segment rows — values sum to Sent.
                          Pending and Failed each carry a help tooltip with
                          the real Meta-side reasons. These two states are
                          the #1 and #2 client support questions ("why is my
                          message stuck?" / "why did it fail?"), so the
                          answer is one click away rather than a back-and-
                          forth with support. */}
                      <TooltipProvider delayDuration={150}>
                        <div className="space-y-1.5 pt-1">
                          {segments.map(seg => (
                            <div key={seg.key} className="flex items-center justify-between">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
                                <span className="text-[13px] font-semibold text-[#334155]">{seg.label}</span>
                                <span className="text-[11px] text-[#94A3B8] hidden sm:inline truncate">— {seg.hint}</span>
                                {seg.helpReasons && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button type="button" className="inline-flex items-center justify-center text-[#94A3B8] hover:text-[#475569] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] rounded-full" aria-label={seg.helpTitle}>
                                        <HelpCircle className="h-3.5 w-3.5" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-[320px] text-[12px] leading-snug" side="top">
                                      <p className="font-semibold mb-1">{seg.helpTitle}</p>
                                      <p className="text-[11px] mb-1.5 text-[#475569]">{seg.helpIntro}</p>
                                      <ul className="text-[11px] text-[#475569] list-disc pl-4 space-y-0.5">
                                        {seg.helpReasons.map((reason, i) => (
                                          <li key={i}>{reason}</li>
                                        ))}
                                      </ul>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                              <div className="flex items-baseline gap-2 flex-shrink-0">
                                <span className="text-[14px] font-bold text-[#0F172A]">{seg.value.toLocaleString()}</span>
                                <span className="text-[11px] font-semibold text-[#64748B] tabular-nums w-10 text-right">{pctOf(seg.value).toFixed(1)}%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </TooltipProvider>

                      {/* Received — separate, inbound. Orthogonal to the outbound funnel. */}
                      <div className="flex items-center justify-between pt-2 border-t border-[#F1F5F9]">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#8B5CF6' }} />
                          <span className="text-[13px] font-semibold text-[#334155]">Received</span>
                          <span className="text-[11px] text-[#94A3B8] hidden sm:inline">— messages your customers sent you</span>
                        </div>
                        <span className="text-[14px] font-bold text-[#0F172A]">{totalReceived.toLocaleString()}</span>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════
            SECTION: REVENUE
            9. COMMERCE STAT TILES + Ads Summary
        ═══════════════════════════════════════════════════ */}
        {isAdmin && (
          <div>
            <SectionHeader label="Revenue" color="#10B981" subtitle="Commerce, ads & payments" />
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">

              {/* Commerce */}
              {commerceConnected ? (
                <Card className="border border-[#E2E8F0] shadow-sm bg-white rounded-xl hover:shadow-md hover:border-[#CBD5E1] transition-all cursor-pointer" onClick={() => navigate('/home/commerce/dashboard')}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-sm" style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}>
                          <ShoppingCart className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="text-[14px] font-bold text-[#0F172A]">Online Store</p>
                          <p className="text-[12px] text-[#64748B] font-medium">Revenue & orders</p>
                        </div>
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-[#94A3B8]" />
                    </div>
                    <p className="text-[28px] font-bold text-[#0F172A] leading-none tracking-tight">
                      ₹{(commerceStats?.total_revenue || 0).toLocaleString("en-IN")}
                    </p>
                    <p className="text-[12px] text-[#475569] mt-2 font-medium">
                      {commerceStats?.total_orders || 0} orders · {commerceStats?.total_products || 0} products
                      {commerceStats?.revenue_change ? (
                        <span className={`ml-1.5 font-bold ${commerceStats.revenue_change >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                          {commerceStats.revenue_change >= 0 ? '+' : ''}{commerceStats.revenue_change.toFixed(1)}%
                        </span>
                      ) : null}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border border-[#E2E8F0] shadow-sm bg-gradient-to-br from-[#FAFBFC] to-[#F1F5F9] rounded-xl">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#E2E8F0] flex items-center justify-center">
                        <ShoppingCart className="h-5 w-5 text-[#64748B]" />
                      </div>
                      <div>
                        <p className="text-[14px] font-bold text-[#0F172A]">Online Store</p>
                        <p className="text-[12px] text-[#64748B] font-medium">Not connected</p>
                      </div>
                    </div>
                    <p className="text-[13px] text-[#475569] mb-3 leading-relaxed">Set up your store to track revenue, orders, and products.</p>
                    <Button size="sm" className="h-8 text-[12px] rounded-lg font-semibold text-white bg-[#10B981] hover:bg-[#059669]" onClick={() => navigate('/home/commerce/dashboard')}>
                      Set Up Store
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Ads Summary */}
              {adsConnected ? (
                <Card className="border border-[#E2E8F0] shadow-sm bg-white rounded-xl hover:shadow-md hover:border-[#CBD5E1] transition-all cursor-pointer" onClick={() => navigate('/home/ads')}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-sm" style={{ background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)' }}>
                          <Megaphone className="h-5 w-5 text-white" />
                        </div>
                        {/* <div>
                          <p className="text-[13px] font-semibold text-[#0F172A]">Meta Ads</p>
                          <p className="text-[10px] text-[#94A3B8]">Ad account active</p>
                        </div> */}
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-[#94A3B8]" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] font-semibold text-[#475569]">Campaigns</span>
                        <span className="text-[16px] font-bold text-[#0F172A]">{adsCampaignCount}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] font-semibold text-[#475569]">Balance</span>
                        <span className="text-[16px] font-bold text-[#0F172A]">
                          {(adsBalanceData as any)?.balance != null
                            ? `${(adsBalanceData as any).currency === 'INR' ? '₹' : '$'}${Number((adsBalanceData as any).balance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                            : '—'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border border-[#E2E8F0] shadow-sm bg-gradient-to-br from-[#FAFBFC] to-[#F1F5F9] rounded-xl">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#E2E8F0] flex items-center justify-center">
                        <Megaphone className="h-5 w-5 text-[#64748B]" />
                      </div>
                      <div>
                        <p className="text-[14px] font-bold text-[#0F172A]">Meta Ads</p>
                        <p className="text-[12px] text-[#64748B] font-medium">Not connected</p>
                      </div>
                    </div>
                    {/* <p className="text-[12px] text-[#64748B] mb-3">Connect your Meta ad account to run & track campaigns.</p>
                    <Button size="sm" className="h-7 text-[11px] rounded-lg font-semibold text-white bg-[#3B82F6] hover:bg-[#1D4ED8]" onClick={() => navigate('/home/ads/setup')}>
                      Connect Ads
                    </Button> */}
                    <div>
                        <p className="text-[14px] font-bold text-[#475569]">Coming soon</p>
                        {/* <p className="text-[10px] text-[#94A3B8]">Not connected</p> */}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Payments */}
              <Card className={`border border-[#E2E8F0] shadow-sm rounded-xl hover:shadow-md hover:border-[#CBD5E1] transition-all cursor-pointer ${razorpayConnected ? 'bg-white' : 'bg-gradient-to-br from-[#FAFBFC] to-[#F1F5F9]'}`} onClick={() => navigate('/home/integrations')}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-sm" style={{ background: razorpayConnected ? 'linear-gradient(135deg, #0EA5E9, #0284C7)' : '#E2E8F0' }}>
                      <CreditCard className={`h-5 w-5 ${razorpayConnected ? 'text-white' : 'text-[#64748B]'}`} />
                    </div>
                    <div>
                      <p className="text-[14px] font-bold text-[#0F172A]">Payments</p>
                      <p className="text-[12px] text-[#64748B] font-medium">Razorpay gateway</p>
                    </div>
                  </div>
                  {razorpayLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : razorpayConnected ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                      <p className="text-[14px] font-semibold text-[#0F172A]">Gateway active</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-[13px] text-[#475569] mb-3 leading-relaxed">Accept payments via Razorpay integration.</p>
                      <Button size="sm" className="h-8 text-[12px] rounded-lg font-semibold text-white bg-[#0EA5E9] hover:bg-[#0284C7]">
                        Connect
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Shopify / WooCommerce */}
              <Card className="border border-[#E2E8F0] shadow-sm bg-white rounded-xl">
                <CardContent className="p-4 space-y-3">
                  {/* Shopify */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${shopifyConnected ? 'bg-[#96BF48]/15' : 'bg-[#F1F5F9]'}`}>
                        <Package className={`h-4.5 w-4.5 ${shopifyConnected ? 'text-[#7BA539]' : 'text-[#64748B]'}`} />
                      </div>
                      <div>
                        <p className="text-[14px] font-bold text-[#0F172A]">Shopify</p>
                        {shopifyConnected && <p className="text-[12px] text-[#475569] font-medium">{shopifyTotal} orders</p>}
                      </div>
                    </div>
                    {shopifyConnected ? (
                      <span className="w-2.5 h-2.5 rounded-full bg-[#22C55E]" />
                    ) : (
                      <Button variant="ghost" size="sm" className="h-7 text-[12px] font-semibold text-[#7BA539] hover:bg-[#96BF48]/10" onClick={() => navigate('/home/integrations')}>
                        Connect
                      </Button>
                    )}
                  </div>
                  <div className="border-t border-[#E2E8F0]" />
                  {/* WooCommerce */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${wooConnected ? 'bg-[#7B51AD]/15' : 'bg-[#F1F5F9]'}`}>
                        <ShoppingCart className={`h-4.5 w-4.5 ${wooConnected ? 'text-[#7B51AD]' : 'text-[#64748B]'}`} />
                      </div>
                      <div>
                        <p className="text-[14px] font-bold text-[#0F172A]">WooCommerce</p>
                        {wooConnected && <p className="text-[12px] text-[#475569] font-medium">{wooTotal} orders</p>}
                      </div>
                    </div>
                    {wooConnected ? (
                      <span className="w-2.5 h-2.5 rounded-full bg-[#22C55E]" />
                    ) : (
                      <Button variant="ghost" size="sm" className="h-7 text-[12px] font-semibold text-[#7B51AD] hover:bg-[#7B51AD]/10" onClick={() => navigate('/home/integrations')}>
                        Connect
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════
            SECTION: OPERATIONS
            10. OPERATIONS & TOOLS (individual cards with metrics)
        ═══════════════════════════════════════════════════ */}
        {isAdmin && (
          <div>
            <SectionHeader label="Operations" color="#F59E0B" subtitle="Automation, AI, scheduling & tools" />
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">

              {/* Automation */}
              <OperationCard
                title="Automation"
                subtitle="WhatsApp workflows"
                icon={Zap}
                gradient="linear-gradient(135deg, #F59E0B, #D97706)"
                connected={automationConnected}
                loading={automationLoading}
                metric={automationTotal}
                metricLabel="workflows"
                emptyTitle="No automations yet"
                emptyDesc="Create automated flows to handle common conversations."
                connectLabel="Create First Flow"
                onClick={() => navigate('/home/automation')}
              />

              {/* AI Agent */}
              <OperationCard
                title="AI Agent"
                subtitle="Conversational AI"
                icon={Bot}
                gradient="linear-gradient(135deg, #0F172A, #334155)"
                connected={aiAgentsConnected}
                loading={aiAgentsLoading}
                metric={aiAgentsCount}
                metricLabel={`${aiAgentsActive} active`}
                emptyTitle="No AI agents"
                emptyDesc="Deploy AI agents to auto-respond on WhatsApp."
                connectLabel="Create Agent"
                onClick={() => navigate('/home/ai-agents')}
                badge={<Badge className="bg-gradient-to-r from-[#0F172A] to-[#475569] text-white border-0 text-[11px] font-bold px-2 py-0.5">AI</Badge>}
              />

              {/* Live Chat */}
              <OperationCard
                title="Live Chat"
                subtitle="Website widget"
                icon={MessageCircle}
                gradient="linear-gradient(135deg, #06B6D4, #0891B2)"
                connected={liveChatConnected}
                loading={liveChatLoading}
                metric={(liveChatChannels as any[])?.length || 0}
                metricLabel="widgets active"
                emptyTitle="No chat widgets"
                emptyDesc="Add a live chat widget to your website."
                connectLabel="Setup Widget"
                onClick={() => navigate('/home/live-chat')}
              />

              {/* Scheduling */}
              <OperationCard
                title="Scheduling"
                subtitle="Bookings & meetings"
                icon={CalendarCheck}
                gradient="linear-gradient(135deg, #A855F7, #7C3AED)"
                connected={schedulingConnected}
                loading={schedulingLoading}
                metric={schedulingData.length}
                metricLabel="event types"
                emptyTitle="No event types"
                emptyDesc="Set up appointment booking and scheduling."
                connectLabel="Setup Booking"
                onClick={() => navigate('/home/scheduling')}
              />

              {/* Templates */}
              <OperationCard
                title="Templates"
                subtitle="WhatsApp messages"
                icon={LayoutTemplate}
                gradient="linear-gradient(135deg, #14B8A6, #0D9488)"
                connected={templatesConnected}
                loading={templatesLoading}
                metric={templatesCount}
                metricLabel={`${templatesApproved} approved`}
                emptyTitle="No templates"
                emptyDesc="Create message templates for campaigns and automation."
                connectLabel="Create Template"
                onClick={() => navigate('/home/templates')}
              />

              {/* WA Flows */}
              <OperationCard
                title="WA Flows"
                subtitle="Interactive forms"
                icon={Workflow}
                gradient="linear-gradient(135deg, #25D366, #128C7E)"
                connected={whatsappFlowsConnected}
                loading={whatsappFlowsLoading}
                metric={whatsappFlowsCount}
                metricLabel="published flows"
                emptyTitle="No flows created"
                emptyDesc="Build WhatsApp forms and interactive flows."
                connectLabel="Create Flow"
                onClick={() => navigate('/home/flows')}
              />
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════
            DATA SUMMARY CARDS (CRM + Messaging Limit + Team)
        ═══════════════════════════════════════════════════ */}
        <div>
          <SectionHeader label="Insights" color="var(--secondary-color)" subtitle="CRM, team & usage" />
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">

            {/* CRM Snapshot */}
            <Card className="border border-[#E2E8F0] shadow-sm bg-white rounded-xl">
              <CardHeader className="pb-3 pt-4 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-[15px] font-bold text-[#0F172A] flex items-center gap-2 tracking-tight">
                    <Users className="h-4 w-4 text-[#3B82F6]" />
                    CRM Snapshot
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="h-7 text-[12px] font-semibold text-[#475569] hover:text-[#0F172A]" onClick={() => navigate('/home/customers')}>
                    View <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {contactStatsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : totalContacts === 0 ? (
                  <EmptyState
                    icon={Users}
                    title="No contacts yet"
                    description="Import or add contacts to build your CRM."
                    actionLabel="Add Contacts"
                    onAction={() => navigate('/home/customers')}
                    compact
                  />
                ) : (
                  <div className="space-y-3">
                    {[
                      { label: 'Total Contacts', value: totalContacts, color: '#3B82F6' },
                      { label: 'New Today', value: newContactsToday, color: '#10B981' },
                      { label: 'Inactive (30d)', value: (contactStatsData as any)?.noInteractivity30Days ?? 0, color: '#F59E0B' },
                      { label: 'Unsubscribed', value: (contactStatsData as any)?.unsubscribed ?? 0, color: '#EF4444' },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                          <span className="text-[13px] font-semibold text-[#475569]">{item.label}</span>
                        </div>
                        <span className="text-[16px] font-bold text-[#0F172A]">{item.value.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Messaging Limit */}
            {messagingUsageInfo && (
              <Card className="border border-[#E2E8F0] shadow-sm bg-white rounded-xl">
                <CardHeader className="pb-3 pt-4 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-[15px] font-bold text-[#0F172A] flex items-center gap-2 tracking-tight">
                      <Send className="h-4 w-4 text-[#8B5CF6]" />
                      Messaging Limit
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                      </span>
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 hover:bg-[#F1F5F9]"
                      onClick={() => refetchUsage()}
                      disabled={isUsageRefetching}
                    >
                      <RefreshCw className={`h-3.5 w-3.5 text-[#475569] ${isUsageRefetching ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="space-y-3">
                    {messagingUsageInfo.tier && (
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] font-semibold text-[#475569]">Tier</span>
                        <Badge variant="outline" className="text-[12px] font-mono font-semibold border-[#CBD5E1] text-[#334155]">{(messagingUsageInfo.tier || '').replace('TIER_', 'Tier ')}</Badge>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-semibold text-[#475569]">Used (24h)</span>
                      <span className="text-[16px] font-bold text-[#0F172A]">{formatNumber(messagingUsageInfo.used ?? 0)}</span>
                    </div>
                    {!messagingUsageInfo.isUnlimited && messagingUsageInfo.limit != null && messagingUsageInfo.limit > 0 && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-[13px] font-semibold text-[#475569]">Limit</span>
                          <span className="text-[16px] font-bold text-[#0F172A]">{formatNumber(messagingUsageInfo.limit)}</span>
                        </div>
                        <div className="w-full h-2.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                          {(() => {
                            const limit = messagingUsageInfo.limit ?? 0;
                            const used = messagingUsageInfo.used ?? 0;
                            const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
                            return (
                              <div
                                className="h-full rounded-full transition-all duration-700"
                                style={{
                                  width: `${pct}%`,
                                  backgroundColor: pct > 80 ? '#EF4444' : pct > 60 ? '#F59E0B' : '#10B981',
                                }}
                              />
                            );
                          })()}
                        </div>
                        <div className="flex items-center justify-between text-[11px] font-semibold text-[#64748B]">
                          <span>0</span>
                          <span>{formatNumber(messagingUsageInfo.limit)}</span>
                        </div>
                      </>
                    )}
                    {messagingUsageInfo.isUnlimited && (
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[12px] font-semibold">Unlimited Plan</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Team */}
            <Card className="border border-[#E2E8F0] shadow-sm bg-white rounded-xl">
              <CardHeader className="pb-3 pt-4 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-[15px] font-bold text-[#0F172A] flex items-center gap-2 tracking-tight">
                    <UserCog className="h-4 w-4 text-[#6366F1]" />
                    Team
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="h-7 text-[12px] font-semibold text-[#475569] hover:text-[#0F172A]" onClick={() => navigate('/home/team-management')}>
                    Manage <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {usersLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-[36px] font-bold text-[#0F172A] leading-none tracking-tight">{teamCount}</p>
                      <p className="text-[13px] text-[#475569] mt-1.5 font-semibold">team member{teamCount !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="flex-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 text-[13px] font-semibold w-full rounded-lg border-[#CBD5E1] text-[#334155] hover:bg-[#F1F5F9] hover:border-[#94A3B8]"
                        onClick={() => navigate('/home/team-management')}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Member
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

      </div>

      {/* Connect-account flow — reused by the Meta banner + Setup Progress "Connect" CTA */}
      <ConnectMetaModal
        open={isConnectDialogOpen}
        onClose={() => setIsConnectDialogOpen(false)}
      />
    </div>
  );
};

// ─── Section Header ───
function SectionHeader({ label, color, subtitle }: { label: string; color: string; subtitle?: string }) {
  return (
    <div className="flex items-baseline gap-2.5 mb-3.5">
      <div className="w-1.5 h-5 rounded-full self-center" style={{ backgroundColor: color }} />
      <h3 className="text-[14px] font-bold text-[#0F172A] uppercase tracking-wider">{label}</h3>
      {subtitle && <span className="text-[12px] text-[#64748B] font-medium">{subtitle}</span>}
    </div>
  );
}

// ─── Empty State Component ───
function EmptyState({
  icon: Icon, title, description, actionLabel, onAction, compact,
}: {
  icon: any; title: string; description: string; actionLabel?: string; onAction?: () => void; compact?: boolean;
}) {
  return (
    <div className={`flex flex-col items-center justify-center text-center ${compact ? 'py-4' : 'py-7'}`}>
      <div className={`${compact ? 'w-10 h-10' : 'w-12 h-12'} rounded-full bg-[#F1F5F9] flex items-center justify-center mb-3`}>
        <Icon className={`${compact ? 'h-5 w-5' : 'h-6 w-6'} text-[#64748B]`} />
      </div>
      <p className={`${compact ? 'text-[14px]' : 'text-[15px]'} font-bold text-[#0F172A] mb-1.5 tracking-tight`}>{title}</p>
      <p className={`${compact ? 'text-[12px]' : 'text-[13px]'} text-[#64748B] mb-3 max-w-[260px] leading-relaxed`}>{description}</p>
      {actionLabel && onAction && (
        <Button
          size="sm"
          variant="outline"
          className={`${compact ? 'h-7 text-[12px]' : 'h-8 text-[13px]'} font-semibold rounded-lg border-[#CBD5E1] text-[#334155] hover:bg-[#F1F5F9] hover:border-[#94A3B8]`}
          onClick={onAction}
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

// ─── Operation Card ───
function OperationCard({
  title, subtitle, icon: Icon, gradient, connected, loading, metric, metricLabel,
  emptyTitle, emptyDesc, connectLabel, onClick, badge,
}: {
  title: string; subtitle: string; icon: any; gradient: string; connected: boolean;
  loading?: boolean; metric?: number; metricLabel?: string;
  emptyTitle: string; emptyDesc: string; connectLabel: string;
  onClick: () => void; badge?: React.ReactNode;
}) {
  return (
    <Card
      className={`group border border-[#E2E8F0] shadow-sm rounded-xl hover:shadow-md hover:border-[#CBD5E1] transition-all cursor-pointer ${connected ? 'bg-white' : 'bg-gradient-to-br from-[#FAFBFC] to-[#F1F5F9]'}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-2.5 mb-3.5">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-sm" style={{ background: connected ? gradient : '#E2E8F0' }}>
            <Icon className={`h-4.5 w-4.5 ${connected ? 'text-white' : 'text-[#64748B]'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-bold text-[#0F172A] tracking-tight">{title}</p>
            <p className="text-[12px] text-[#64748B] font-medium mt-0.5">{subtitle}</p>
          </div>
          {badge || (connected && <span className="w-2.5 h-2.5 rounded-full bg-[#22C55E] flex-shrink-0" />)}
        </div>
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : connected ? (
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[26px] font-bold text-[#0F172A] leading-none tracking-tight">{metric}</p>
              <p className="text-[12px] text-[#475569] mt-1.5 font-semibold">{metricLabel}</p>
            </div>
            <ArrowUpRight className="h-4 w-4 text-[#94A3B8] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#475569] transition-all" />
          </div>
        ) : (
          <div>
            <p className="text-[13px] text-[#475569] mb-3 leading-relaxed">{emptyDesc}</p>
            <Button
              size="sm"
              className="h-8 text-[12px] rounded-lg font-semibold text-white shadow-sm"
              style={{ background: gradient }}
              onClick={(e) => { e.stopPropagation(); onClick(); }}
            >
              {connectLabel}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
